import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { put } from '@/api/client';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { useAuthStore } from '@/store/authStore';
import { toast } from '@/store/toastStore';
import { Check, Sparkles } from 'lucide-react';

const TOTAL_STEPS = 5;

/* 관심 분야 — 노출 순서: 국내주식 → 해외주식 → 코인 → 부동산 → 거시경제 (AI·퀀트 제외) */
const INTERESTS = [
  { id: 'stock', label: '국내주식' },
  { id: 'global', label: '해외주식' },
  { id: 'coin', label: '코인' },
  { id: 'realestate', label: '부동산' },
  { id: 'macro', label: '거시경제' },
];

type InvestCategory = 'domestic' | 'usa' | 'coin' | 'etc';
const INVEST_CATEGORIES: Array<{ key: InvestCategory; label: string }> = [
  { key: 'domestic', label: '국내주식' },
  { key: 'usa', label: '미국주식' },
  { key: 'coin', label: '코인' },
  { key: 'etc', label: '기타' },
];

/**
 * 투자 기간 옵션 1~10년차
 * 매년 1월 1일 기준 자동으로 한 칸씩 올라감 (startYear 저장 방식)
 */
function buildYearOptions(currentYear: number) {
  const opts: { years: number; label: string; startYear: number }[] = [];
  for (let y = 1; y <= 9; y++) {
    opts.push({ years: y, label: `${y}년차`, startYear: currentYear - y + 1 });
  }
  opts.push({ years: 10, label: '10년 이상', startYear: currentYear - 9 });
  return opts;
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const { me, setMe } = useAuthStore();
  const [step, setStep] = useState(1);
  const [nickname, setNickname] = useState(me?.nickname ?? '');
  const overflowAlertedRef = useRef(false);
  const NICK_MAX = 10;
  const [interests, setInterests] = useState<string[]>([]);
  const [investYears, setInvestYears] = useState<Record<InvestCategory, number | null>>({
    domestic: null,
    usa: null,
    coin: null,
    etc: null,
  });
  const currentYear = new Date().getFullYear();
  const YEAR_OPTIONS = buildYearOptions(currentYear);

  const mutation = useMutation({
    mutationFn: () =>
      put('/users/me/onboarding', {
        nickname,
        interestTags: interests,
        investYearDomestic: investYears.domestic,
        investYearUsa: investYears.usa,
        investYearCoin: investYears.coin,
        investYearEtc: investYears.etc,
      }),
    onSuccess: () => {
      if (me) setMe({ ...me, nickname, onboardingDone: true });
      setStep(5);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message ?? '저장에 실패했어요.');
    },
  });

  const toggleInterest = (id: string) => {
    setInterests((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : prev.length < 3 ? [...prev, id] : prev,
    );
  };

  return (
    /* 모바일: 풀스크린 단순 레이아웃 — 화면 상단 정렬, 카드 없음, 시스템 배경
       PC(md+): 카드 디자인 — 화면 중앙 정렬, 흰 카드 + 테두리 + 그림자, 그라데이션 배경 */
    <div className="flex min-h-screen flex-col items-stretch justify-start bg-[var(--color-bg)] md:items-center md:justify-center md:bg-gradient-to-b md:from-white md:to-[#eff6ff] md:px-6 md:py-12">
      <div className="w-full flex-1 px-5 py-8 md:flex-none md:max-w-2xl md:px-0 md:py-0">
        <div className="md:rounded-3xl md:border md:border-[var(--color-border)] md:bg-[var(--color-surface)] md:p-12 md:shadow-lg lg:p-16">
          {/* 진행바 */}
          <div className="mb-8 flex gap-1.5 md:mb-10">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  i < step ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-border)]'
                }`}
              />
            ))}
          </div>

        {/* Step 1: 환영 */}
        {step === 1 && (
          <div className="text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[var(--color-primary)]/10 md:h-24 md:w-24">
              <span className="text-4xl md:text-5xl">🎉</span>
            </div>
            <h1 className="text-2xl font-extrabold md:text-3xl">시세공간에 오신 걸 환영해요!</h1>
            <p className="mt-3 text-sm leading-relaxed text-[var(--color-text-sub)] md:text-base">
              주식·코인·재테크를 한곳에서 소통하는
              <br />
              투자 커뮤니티 시세공간이에요.
              <br />
              간단한 설정만 하면 바로 시작할 수 있어요.
            </p>
            <Button fullWidth size="lg" className="mt-8" onClick={() => setStep(2)}>
              시작하기
            </Button>
          </div>
        )}

        {/* Step 2: 닉네임 */}
        {step === 2 && (
          <div>
            <h1 className="text-2xl font-extrabold md:text-3xl">닉네임을 정해주세요</h1>
            <p className="mt-2 text-sm text-[var(--color-text-sub)] md:text-base">
              2~10자, 한글/영문/숫자/밑줄(_)만 사용 가능
            </p>
            <Input
              value={nickname}
              onChange={() => {
                /* onInput 에서 처리 */
              }}
              onInput={(e) => {
                const raw = (e.target as HTMLInputElement).value;
                if (raw.length > NICK_MAX) {
                  if (!overflowAlertedRef.current) {
                    overflowAlertedRef.current = true;
                    toast.error(`닉네임은 최대 ${NICK_MAX}글자까지 입력할 수 있어요.`);
                  }
                } else {
                  overflowAlertedRef.current = false;
                }
                setNickname(raw.slice(0, NICK_MAX));
              }}
              maxLength={NICK_MAX}
              placeholder="닉네임 입력"
              className="mt-6"
            />
            <p className="mt-2 text-xs text-[var(--color-text-sub)]">
              한글 기준 최대 {NICK_MAX}글자까지 입력 가능해요.
            </p>
            <div className="mt-1 text-xs text-[var(--color-text-sub)]">
              {nickname.length}/{NICK_MAX}자
            </div>
            <Button
              fullWidth
              size="lg"
              className="mt-4"
              disabled={nickname.length < 2 || !/^[가-힣a-zA-Z0-9_]+$/.test(nickname)}
              onClick={() => setStep(3)}
            >
              다음
            </Button>
          </div>
        )}

        {/* Step 3: 관심분야 */}
        {step === 3 && (
          <div>
            <h1 className="text-2xl font-extrabold md:text-3xl">관심 분야를 선택해주세요</h1>
            <p className="mt-2 text-sm text-[var(--color-text-sub)] md:text-base">최대 3개까지 선택</p>
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {INTERESTS.map((i) => {
                const active = interests.includes(i.id);
                return (
                  <button
                    key={i.id}
                    onClick={() => toggleInterest(i.id)}
                    className={`relative rounded-xl border-2 p-4 text-center transition-all ${
                      active
                        ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5'
                        : 'border-[var(--color-border)] bg-[var(--color-surface)]'
                    }`}
                  >
                    {active && (
                      <div className="absolute right-2 top-2">
                        <Check className="h-4 w-4 text-[var(--color-primary)]" />
                      </div>
                    )}
                    <div className="text-sm font-semibold">{i.label}</div>
                  </button>
                );
              })}
            </div>
            <Button
              fullWidth
              size="lg"
              className="mt-6"
              disabled={interests.length === 0}
              onClick={() => setStep(4)}
            >
              다음
            </Button>
          </div>
        )}

        {/* Step 4: 투자 기간 (4개 분야별, 선택 가능) */}
        {step === 4 && (
          <div>
            <h1 className="text-2xl font-extrabold md:text-3xl">투자 기간은 얼마나 되나요?</h1>
            <p className="mt-2 text-sm leading-relaxed text-[var(--color-text-sub)] md:text-base">
              매년 1월 1일 기준으로 자동 갱신돼요. 분야별로 선택할 수 있고, 선택하지 않아도 괜찮아요.
            </p>
            <div className="mt-6 space-y-5">
              {INVEST_CATEGORIES.map((cat) => {
                const current = investYears[cat.key];
                return (
                  <div key={cat.key}>
                    <div className="mb-2 flex items-center justify-between text-sm font-semibold">
                      <span>{cat.label}</span>
                      {current !== null && (
                        <button
                          type="button"
                          onClick={() =>
                            setInvestYears((prev) => ({ ...prev, [cat.key]: null }))
                          }
                          className="text-xs font-normal text-[var(--color-text-sub)] hover:underline"
                        >
                          선택 해제
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {YEAR_OPTIONS.map((opt) => {
                        const active = current === opt.startYear;
                        return (
                          <button
                            key={opt.years}
                            type="button"
                            onClick={() =>
                              setInvestYears((prev) => ({ ...prev, [cat.key]: opt.startYear }))
                            }
                            className={`rounded-xl border-2 py-3 text-center text-sm font-medium transition-all ${
                              active
                                ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/5 text-[var(--color-primary)]'
                                : 'border-[var(--color-border)] bg-[var(--color-surface)]'
                            }`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              fullWidth
              size="lg"
              className="mt-6"
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              완료
            </Button>
          </div>
        )}

        {/* Step 5: 완료 — 모의투자 서비스 미제공 상태이므로 문구에서 제외 */}
        {step === 5 && (
          <div className="text-center">
            <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-[var(--color-primary)]/10 md:h-24 md:w-24">
              <Sparkles className="h-10 w-10 text-[var(--color-primary)] md:h-12 md:w-12" />
            </div>
            <h1 className="text-2xl font-extrabold md:text-3xl">회원가입 완료</h1>
            <div className="mt-4 inline-flex items-center gap-2 rounded-xl bg-amber-50 px-5 py-3 text-lg font-bold text-amber-600">
              🎁 가입 보너스 +200P 지급!
            </div>
            <p className="mt-4 text-sm leading-relaxed text-[var(--color-text-sub)] md:text-base">
              시세공간에서 다양한 투자 이야기를 나눠보세요.
            </p>
            <Button
              fullWidth
              size="lg"
              className="mt-8"
              onClick={() => navigate('/', { replace: true })}
            >
              시세공간 시작하기
            </Button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
}
